import { z } from 'zod'

import { formatSiteDate, getTodayInTimezone } from '@hapana/shared/utils/date.util'
import {
  CopyStatus,
  ESignConsentProfile,
  EvidenceStatus,
  PendingItemHandoffCapability,
  PendingItemKind,
  PendingItemsChannel,
  PendingItemsErrorCode,
  PendingItemsHandoffStatus,
  PolicyAcceptanceMethod,
  PolicyComplianceErrorCode,
  SignerCapacity,
} from '@hapana/shared/schemas/policy-compliance/policy-compliance-enums'
import {
  PENDING_ITEMS_ACCESS_CODE_LENGTH,
  PendingItemsDateOrder,
  PendingItemsStepStatus,
  type PendingItemsPublicLocation,
} from '@hapana/shared/schemas/policy-compliance/pending-items-public-schemas'
import {
  getDateFormatPattern,
  getSiteDateFormat,
  type DateFormatStyle,
} from '@hapana/shared/web/utils/date-format-utils'

import type { PendingItemsGuardianDeclarationInput } from '_services/pending-items-public.service'
import {
  PendingItemsRequestError,
  type PendingItemsLinkedItem,
  type PendingItemsPublicItem,
  type PendingItemsPublicModel,
  type PendingItemsReceiptDocument,
} from '_services/pending-items-public.service'

export const PENDING_ITEMS_KEY = 'pendingItemsPublic'

export const DESK_IDLE_TIMEOUT_MS = 120_000

const MONTH_FIRST_LOCALES = new Set(['en-US'])

export type PendingItemsDateLocation = Pick<
  PendingItemsPublicLocation,
  'timezone' | 'dateFormat' | 'locale'
>

const resolveDatePattern = (location: PendingItemsDateLocation, style: DateFormatStyle): string => {
  if (location.dateFormat) {
    return getDateFormatPattern(location.dateFormat, style)
  }
  if (location.locale) {
    return getDateFormatPattern(
      MONTH_FIRST_LOCALES.has(location.locale)
        ? PendingItemsDateOrder.MONTH_FIRST
        : PendingItemsDateOrder.DAY_FIRST,
      style
    )
  }
  return getSiteDateFormat(style)
}

export const formatPendingItemsDate = (
  value: string | null | undefined,
  style: DateFormatStyle,
  location: PendingItemsDateLocation
): string =>
  formatSiteDate(value, style, location.timezone ?? undefined, resolveDatePattern(location, style))

const FALLBACK_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone

export const resolvePendingItemsToday = (location: Pick<PendingItemsDateLocation, 'timezone'>) =>
  getTodayInTimezone(location.timezone ?? FALLBACK_TIMEZONE)

export const DESK_IDLE_TIMEOUT_MINUTES = DESK_IDLE_TIMEOUT_MS / 60_000

export const DESK_IDLE_WARNING_MS = 30_000
export const MIN_SIGNER_AGE_YEARS = 18

export const PendingItemsView = {
  LOADING: 'LOADING',
  LOAD_ERROR: 'LOAD_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  TERMINAL: 'TERMINAL',
  RECEIPT: 'RECEIPT',
  VERIFY: 'VERIFY',
  GUARDIAN: 'GUARDIAN',
  CONSENT: 'CONSENT',
  ITEMS: 'ITEMS',
  PRESENCE_REQUIRED: 'PRESENCE_REQUIRED',
  ACCESS_REVOKED: 'ACCESS_REVOKED',
} as const
export type PendingItemsView = (typeof PendingItemsView)[keyof typeof PendingItemsView]

export const TerminalState = {
  SUPERSEDED: 'SUPERSEDED',
  EXPIRED: 'EXPIRED',
  CANCELLED: 'CANCELLED',
  LOCKED: 'LOCKED',
  FAILED: 'FAILED',
} as const
export type TerminalState = (typeof TerminalState)[keyof typeof TerminalState]

const TERMINAL_BY_STATUS: Partial<Record<PendingItemsHandoffStatus, TerminalState>> = {
  [PendingItemsHandoffStatus.SUPERSEDED]: TerminalState.SUPERSEDED,
  [PendingItemsHandoffStatus.EXPIRED]: TerminalState.EXPIRED,
  [PendingItemsHandoffStatus.CANCELLED]: TerminalState.CANCELLED,
  [PendingItemsHandoffStatus.LOCKED]: TerminalState.LOCKED,
  [PendingItemsHandoffStatus.FAILED]: TerminalState.FAILED,
}

export const resolveTerminalState = (status: PendingItemsHandoffStatus): TerminalState | null =>
  TERMINAL_BY_STATUS[status] ?? null

const NOT_FOUND_STATUSES = new Set([401, 404])

export const isPendingItemsNotFound = (error: unknown): boolean =>
  error instanceof PendingItemsRequestError &&
  (NOT_FOUND_STATUSES.has(error.status) || error.code === PendingItemsErrorCode.FEATURE_UNAVAILABLE)

export const PresenceRefusalReason = {
  MISSING: 'MISSING',
  MISMATCH: 'MISMATCH',
  DEVICE: 'DEVICE',
} as const
export type PresenceRefusalReason =
  (typeof PresenceRefusalReason)[keyof typeof PresenceRefusalReason]

export const GUARDIAN_AUTHORITY_REVOKED_RESULT = 'GUARDIAN_AUTHORITY_REVOKED'

const readErrorDetail = (error: unknown, key: string): string | null => {
  if (!(error instanceof PendingItemsRequestError)) {
    return null
  }
  const value = error.details?.[key]
  return typeof value === 'string' ? value : null
}

export const isPresenceRequired = (error: unknown): boolean =>
  error instanceof PendingItemsRequestError &&
  error.code === PendingItemsErrorCode.PRESENCE_REQUIRED

export const isGuardianAuthorityRevoked = (error: unknown): boolean =>
  error instanceof PendingItemsRequestError &&
  error.code === PendingItemsErrorCode.LINK_INACTIVE &&
  readErrorDetail(error, 'resultCode') === GUARDIAN_AUTHORITY_REVOKED_RESULT

export const isHandoffMismatch = (error: unknown): boolean =>
  error instanceof PendingItemsRequestError && error.code === PendingItemsErrorCode.HANDOFF_MISMATCH

const KNOWN_PRESENCE_REASONS: ReadonlySet<string> = new Set(Object.values(PresenceRefusalReason))

export const resolvePresenceReason = (error: unknown): PresenceRefusalReason => {
  const reason = readErrorDetail(error, 'reason')
  return reason && KNOWN_PRESENCE_REASONS.has(reason)
    ? (reason as PresenceRefusalReason)
    : PresenceRefusalReason.MISSING
}

export const resolvePendingItemsView = (input: {
  model: PendingItemsPublicModel | undefined
  isLoading: boolean
  error: unknown
  guardianDeclared: boolean
}): PendingItemsView => {
  if (isPresenceRequired(input.error)) {
    return PendingItemsView.PRESENCE_REQUIRED
  }
  if (isGuardianAuthorityRevoked(input.error)) {
    return PendingItemsView.ACCESS_REVOKED
  }
  if (input.model) {
    if (input.model.status === PendingItemsHandoffStatus.COMPLETED) {
      return PendingItemsView.RECEIPT
    }
    if (resolveTerminalState(input.model.status)) {
      return PendingItemsView.TERMINAL
    }
    if (input.model.steps.identity === PendingItemsStepStatus.REQUIRED) {
      return PendingItemsView.VERIFY
    }
    if (
      input.model.steps.guardianDeclaration === PendingItemsStepStatus.REQUIRED &&
      !input.guardianDeclared
    ) {
      return PendingItemsView.GUARDIAN
    }
    if (input.model.steps.electronicConsent === PendingItemsStepStatus.REQUIRED) {
      return PendingItemsView.CONSENT
    }
    return PendingItemsView.ITEMS
  }
  if (input.isLoading) {
    return PendingItemsView.LOADING
  }
  return isPendingItemsNotFound(input.error)
    ? PendingItemsView.NOT_FOUND
    : PendingItemsView.LOAD_ERROR
}

export type GroupedPendingItems = {
  blocking: PendingItemsPublicItem[]
  actionable: PendingItemsPublicItem[]
  upcoming: PendingItemsPublicItem[]
  linked: PendingItemsLinkedItem[]
  done: Array<{ itemKey: string; title: string }>
}

const EQUIPMENT_GATE_KINDS: ReadonlySet<PendingItemKind> = new Set([
  PendingItemKind.SITE_POLICY,
  PendingItemKind.SKIN_TYPE_FORM,
  PendingItemKind.AGE_VERIFICATION,
])

export const isEquipmentGateItem = (item: PendingItemsPublicItem): boolean =>
  item.kind === PendingItemKind.SITE_POLICY
    ? item.requiredForEquipment !== false
    : EQUIPMENT_GATE_KINDS.has(item.kind)

export const splitBlockingItems = (
  items: PendingItemsPublicItem[]
): { visit: PendingItemsPublicItem[]; equipment: PendingItemsPublicItem[] } => ({
  visit: items.filter((item) => !isEquipmentGateItem(item)),
  equipment: items.filter(isEquipmentGateItem),
})

export const groupPendingItems = (model: PendingItemsPublicModel): GroupedPendingItems => {
  const blockingKeys = new Set(model.blockingItems.map((item) => item.itemKey))
  return {
    blocking: model.blockingItems,
    actionable: model.actionableItems.filter((item) => !blockingKeys.has(item.itemKey)),
    upcoming: model.upcomingItems,
    linked: model.linkedItems,
    done: model.doneItems,
  }
}

export const hasOpenItems = (groups: GroupedPendingItems): boolean =>
  groups.blocking.length > 0 ||
  groups.actionable.length > 0 ||
  groups.linked.some((item) => !item.done)

export const ItemAction = {
  SIGN: 'SIGN',
  ACCEPT: 'ACCEPT',
  ANSWER: 'ANSWER',
  OPEN_LINK: 'OPEN_LINK',
  DESK_ONLY: 'DESK_ONLY',
} as const
export type ItemAction = (typeof ItemAction)[keyof typeof ItemAction]

export const resolveItemAction = (item: PendingItemsPublicItem): ItemAction => {
  if (item.capability !== PendingItemHandoffCapability.PENDING_ITEMS) {
    return ItemAction.OPEN_LINK
  }
  if (item.kind === PendingItemKind.SKIN_TYPE_FORM && item.form) {
    return ItemAction.ANSWER
  }
  if (item.kind === PendingItemKind.SITE_POLICY && item.policyRevisionId && item.revisionSha256) {
    return item.acceptanceMethod === PolicyAcceptanceMethod.CHECKBOX
      ? ItemAction.ACCEPT
      : ItemAction.SIGN
  }
  return ItemAction.DESK_ONLY
}

export const resolveItemStatusKey = (
  item: PendingItemsPublicItem,
  isBlocking: boolean
): { key: string; date: string | null } => {
  if (isBlocking) {
    return {
      key: isEquipmentGateItem(item)
        ? `${PENDING_ITEMS_KEY}.items.status.required`
        : `${PENDING_ITEMS_KEY}.items.status.requiredBeforeVisit`,
      date: null,
    }
  }
  if (item.validUntil) {
    return { key: `${PENDING_ITEMS_KEY}.items.status.renewEarly`, date: item.validUntil }
  }
  if (item.dueOn) {
    return { key: `${PENDING_ITEMS_KEY}.items.status.dueOn`, date: item.dueOn }
  }
  return { key: `${PENDING_ITEMS_KEY}.items.status.optional`, date: null }
}

export const DobField = { DAY: 'day', MONTH: 'month', YEAR: 'year' } as const
export type DobField = (typeof DobField)[keyof typeof DobField]

export const resolveDobFieldOrder = (profile: ESignConsentProfile): DobField[] =>
  profile === ESignConsentProfile.US_ACCESS_CODE
    ? [DobField.MONTH, DobField.DAY, DobField.YEAR]
    : [DobField.DAY, DobField.MONTH, DobField.YEAR]

export type DobParts = { day: string; month: string; year: string }

const pad = (value: string, length: number) => value.trim().padStart(length, '0')

export const composeDateOfBirth = (parts: DobParts): string | null => {
  const day = Number(parts.day)
  const month = Number(parts.month)
  const year = Number(parts.year)
  if (!Number.isInteger(day) || !Number.isInteger(month) || !Number.isInteger(year)) {
    return null
  }
  if (parts.year.trim().length !== 4 || month < 1 || month > 12 || day < 1) {
    return null
  }
  const candidate = new Date(Date.UTC(year, month - 1, day))
  if (candidate.getUTCMonth() !== month - 1 || candidate.getUTCDate() !== day) {
    return null
  }
  return `${pad(parts.year, 4)}-${pad(parts.month, 2)}-${pad(parts.day, 2)}`
}

export const DobErrorCode = {
  REQUIRED: 'required',
  INVALID: 'invalid',
  INVALID_MONTH_FIRST: 'invalidMonthFirst',
  FUTURE: 'future',
} as const

export const buildDobSchema = (today: string, profile: ESignConsentProfile) =>
  z.object({ day: z.string(), month: z.string(), year: z.string() }).superRefine((parts, ctx) => {
    if (!parts.day.trim() || !parts.month.trim() || !parts.year.trim()) {
      ctx.addIssue({ code: 'custom', message: DobErrorCode.REQUIRED, path: ['year'] })
      return
    }
    const iso = composeDateOfBirth(parts)
    if (!iso) {
      ctx.addIssue({
        code: 'custom',
        message:
          resolveDobFieldOrder(profile)[0] === DobField.MONTH
            ? DobErrorCode.INVALID_MONTH_FIRST
            : DobErrorCode.INVALID,
        path: ['year'],
      })
      return
    }
    if (iso > today) {
      ctx.addIssue({ code: 'custom', message: DobErrorCode.FUTURE, path: ['year'] })
    }
  })

export const GuardianErrorCode = {
  NAME_REQUIRED: 'nameRequired',
  RELATIONSHIP_REQUIRED: 'relationshipRequired',
  AUTHORITY_REQUIRED: 'authorityRequired',
} as const

export const guardianDeclarationFormSchema = z.object({
  fullName: z.string().trim().min(1, GuardianErrorCode.NAME_REQUIRED).max(200),
  relationship: z.enum(['PARENT', 'LEGAL_GUARDIAN'], {
    error: GuardianErrorCode.RELATIONSHIP_REQUIRED,
  }),
  authorityDeclared: z.literal(true, { error: GuardianErrorCode.AUTHORITY_REQUIRED }),
})
export type GuardianDeclarationFormValues = z.infer<typeof guardianDeclarationFormSchema>

export const ConsentErrorCode = {
  AGREE_REQUIRED: 'agreeRequired',
  EMAIL_INVALID: 'emailInvalid',
  CODE_REQUIRED: 'codeRequired',
  CODE_FORMAT: 'codeFormat',
} as const

export const buildConsentFormSchema = (accessCodeRequired: boolean) =>
  z
    .object({
      agreed: z.boolean(),
      copiesEmail: z.string().trim().max(320),
      accessCode: z.string().trim(),
    })
    .superRefine((values, ctx) => {
      if (!values.agreed) {
        ctx.addIssue({ code: 'custom', message: ConsentErrorCode.AGREE_REQUIRED, path: ['agreed'] })
      }
      if (values.copiesEmail && !z.email().safeParse(values.copiesEmail).success) {
        ctx.addIssue({
          code: 'custom',
          message: ConsentErrorCode.EMAIL_INVALID,
          path: ['copiesEmail'],
        })
      }
      if (!accessCodeRequired) {
        return
      }
      if (!values.accessCode) {
        ctx.addIssue({
          code: 'custom',
          message: ConsentErrorCode.CODE_REQUIRED,
          path: ['accessCode'],
        })
        return
      }
      if (
        !new RegExp(`^[A-Za-z0-9]{${PENDING_ITEMS_ACCESS_CODE_LENGTH}}$`).test(values.accessCode)
      ) {
        ctx.addIssue({
          code: 'custom',
          message: ConsentErrorCode.CODE_FORMAT,
          path: ['accessCode'],
        })
      }
    })
export type ConsentFormValues = { agreed: boolean; copiesEmail: string; accessCode: string }

export const SignErrorCode = {
  NAME_REQUIRED: 'nameRequired',
  SIGNATURE_REQUIRED: 'signatureRequired',
  ACCEPT_REQUIRED: 'acceptRequired',
} as const

export const SignatureModeOption = { DRAW: 'DRAW', TYPE: 'TYPE' } as const
export type SignatureModeOption = (typeof SignatureModeOption)[keyof typeof SignatureModeOption]

export const buildPolicySignFormSchema = (requiresSignature: boolean) =>
  z
    .object({
      typedName: z.string().trim().min(1, SignErrorCode.NAME_REQUIRED).max(200),
      mode: z.enum(SignatureModeOption),
      drawn: z.boolean(),
      accepted: z.boolean(),
    })
    .superRefine((values, ctx) => {
      if (requiresSignature && values.mode === SignatureModeOption.DRAW && !values.drawn) {
        ctx.addIssue({
          code: 'custom',
          message: SignErrorCode.SIGNATURE_REQUIRED,
          path: ['drawn'],
        })
      }
      if (!requiresSignature && !values.accepted) {
        ctx.addIssue({ code: 'custom', message: SignErrorCode.ACCEPT_REQUIRED, path: ['accepted'] })
      }
    })
export type PolicySignFormValues = {
  typedName: string
  mode: SignatureModeOption
  drawn: boolean
  accepted: boolean
}

export const readErrorCode = (error: unknown): string | null =>
  error instanceof PendingItemsRequestError ? error.code : null

export const readAttemptsRemaining = (error: unknown): number | null => {
  if (!(error instanceof PendingItemsRequestError)) {
    return null
  }
  const value = error.details?.attemptsRemaining
  return typeof value === 'number' ? value : null
}

const SUBMIT_ERROR_KEYS: Partial<Record<string, string>> = {
  [PendingItemsErrorCode.IDENTITY_MISMATCH]: 'identityMismatch',
  [PendingItemsErrorCode.IDENTITY_LOCKED]: 'identityLocked',
  [PendingItemsErrorCode.ACCESS_CODE_REQUIRED]: 'accessCodeRequired',
  [PendingItemsErrorCode.ACCESS_CODE_MISMATCH]: 'accessCodeMismatch',
  [PendingItemsErrorCode.TYPED_NAME_MISMATCH]: 'typedNameMismatch',
  [PendingItemsErrorCode.SIGNATURE_REQUIRED]: 'signatureRequired',
  [PendingItemsErrorCode.UPLOAD_INVALID]: 'uploadInvalid',
  [PendingItemsErrorCode.CONSENT_REQUIRED]: 'consentRequired',
  [PendingItemsErrorCode.DISCLOSURE_CHANGED]: 'disclosureChanged',
  [PendingItemsErrorCode.GUARDIAN_DECLARATION_INVALID]: 'guardianDeclarationInvalid',
  [PendingItemsErrorCode.CAPABILITY_REFUSED]: 'capabilityRefused',
  [PendingItemsErrorCode.RATE_LIMITED]: 'rateLimited',
  [PendingItemsErrorCode.CSRF_REJECTED]: 'csrfRejected',
  [PendingItemsErrorCode.HANDOFF_MISMATCH]: 'handoffMismatch',
  [PendingItemsErrorCode.PRESENCE_REQUIRED]: 'presenceRequired',
  [PolicyComplianceErrorCode.IDEMPOTENCY_CONFLICT]: 'idempotencyConflict',
}

export const resolveSubmitErrorKey = (error: unknown): string =>
  `${PENDING_ITEMS_KEY}.errors.${SUBMIT_ERROR_KEYS[readErrorCode(error) ?? ''] ?? 'generic'}`

export const isPolicyVersionChanged = (error: unknown): boolean =>
  readErrorCode(error) === PolicyComplianceErrorCode.POLICY_VERSION_CHANGED

export const isLinkInactive = (error: unknown): boolean =>
  readErrorCode(error) === PendingItemsErrorCode.LINK_INACTIVE ||
  readErrorCode(error) === PendingItemsErrorCode.PRESENCE_REQUIRED ||
  readErrorCode(error) === PendingItemsErrorCode.IDENTITY_LOCKED ||
  isPendingItemsNotFound(error)

export const isGuardianDeclarationRejected = (error: unknown): boolean =>
  readErrorCode(error) === PendingItemsErrorCode.GUARDIAN_DECLARATION_INVALID

export const isConsentMissing = (error: unknown): boolean =>
  readErrorCode(error) === PendingItemsErrorCode.CONSENT_REQUIRED ||
  readErrorCode(error) === PendingItemsErrorCode.DISCLOSURE_CHANGED

export const isIdempotencyConflict = (error: unknown): boolean =>
  readErrorCode(error) === PolicyComplianceErrorCode.IDEMPOTENCY_CONFLICT

export const needsRefreshAfterSubmit = (error: unknown): boolean =>
  isLinkInactive(error) || isConsentMissing(error) || isIdempotencyConflict(error)

export const createSubmissionId = (): string => crypto.randomUUID().replace(/-/g, '')

export const ReceiptCopyState = {
  EVIDENCE_FAILED: 'evidenceFailed',
  PREPARING: 'preparing',
  SENT: 'sent',
  FAILED: 'failed',
  DOWNLOAD_ONLY: 'downloadOnly',
} as const
export type ReceiptCopyState = (typeof ReceiptCopyState)[keyof typeof ReceiptCopyState]

export const resolveReceiptCopyState = (
  document: PendingItemsReceiptDocument
): ReceiptCopyState => {
  if (document.evidenceStatus === EvidenceStatus.FAILED) {
    return ReceiptCopyState.EVIDENCE_FAILED
  }
  if (document.copyStatus === CopyStatus.SENT) {
    return ReceiptCopyState.SENT
  }
  if (document.copyStatus === CopyStatus.FAILED || document.copyStatus === CopyStatus.BOUNCED) {
    return ReceiptCopyState.FAILED
  }
  if (
    document.evidenceStatus !== EvidenceStatus.READY ||
    document.copyStatus === CopyStatus.QUEUED
  ) {
    return ReceiptCopyState.PREPARING
  }
  return ReceiptCopyState.DOWNLOAD_ONLY
}

export const canDownloadReceiptDocument = (document: PendingItemsReceiptDocument): boolean =>
  document.evidenceStatus === EvidenceStatus.READY

export const IDLE_CHANNELS: ReadonlySet<string> = new Set([
  PendingItemsChannel.DESK,
  PendingItemsChannel.KIOSK,
])

export const isIdleGuardedChannel = (channel: string | undefined): boolean =>
  !!channel && IDLE_CHANNELS.has(channel)

export const DeskPhase = {
  ACTIVE: 'ACTIVE',
  WARNING: 'WARNING',
  LOCKED: 'LOCKED',
} as const
export type DeskPhase = (typeof DeskPhase)[keyof typeof DeskPhase]

export type DeskIdleState = { phase: DeskPhase; resetCount: number }

export type DeskIdleEvent =
  | { type: 'IDLE_ELAPSED' }
  | { type: 'WARNING_ELAPSED' }
  | { type: 'EXTEND' }
  | { type: 'LOCK' }

export const INITIAL_DESK_IDLE_STATE: DeskIdleState = { phase: DeskPhase.ACTIVE, resetCount: 0 }

export const reduceDeskIdle = (state: DeskIdleState, event: DeskIdleEvent): DeskIdleState => {
  switch (event.type) {
    case 'IDLE_ELAPSED':
      return state.phase === DeskPhase.ACTIVE ? { ...state, phase: DeskPhase.WARNING } : state
    case 'WARNING_ELAPSED':
    case 'LOCK':
      return state.phase === DeskPhase.LOCKED
        ? state
        : { phase: DeskPhase.LOCKED, resetCount: state.resetCount + 1 }
    case 'EXTEND':
      return state.phase === DeskPhase.WARNING ? { ...state, phase: DeskPhase.ACTIVE } : state
  }
}

export const isGuardianRecipient = (model: PendingItemsPublicModel): boolean =>
  model.recipient.capacity === SignerCapacity.GUARDIAN

export const isAwaitingGuardian = (model: PendingItemsPublicModel): boolean =>
  !isGuardianRecipient(model) && model.awaitingGuardian === true

export const resolveIntroKey = (model: PendingItemsPublicModel): string => {
  if (isGuardianRecipient(model)) {
    return `${PENDING_ITEMS_KEY}.items.introGuardian`
  }
  const blocking = splitBlockingItems(model.blockingItems)
  if (blocking.equipment.length > 0) {
    return blocking.visit.length > 0
      ? `${PENDING_ITEMS_KEY}.items.introMixed`
      : `${PENDING_ITEMS_KEY}.items.introEquipment`
  }
  return blocking.visit.length > 0
    ? `${PENDING_ITEMS_KEY}.items.intro`
    : `${PENDING_ITEMS_KEY}.items.introOptional`
}

export type PendingItemsFlowState = {
  guardian: PendingItemsGuardianDeclarationInput | null
  guardianRejected: boolean
  activeItemKey: string | null
  completed: boolean
  receiptUrl: string | null
  updatedTitle: string | null
  linkedFeedbackKey: string | null
}

export type PendingItemsFlowEvent =
  | { type: 'DECLARE_GUARDIAN'; declaration: PendingItemsGuardianDeclarationInput }
  | { type: 'REJECT_GUARDIAN' }
  | { type: 'OPEN_ITEM'; itemKey: string }
  | { type: 'CLOSE_ITEM' }
  | { type: 'SAVED'; completed: boolean; receiptUrl: string | null }
  | { type: 'VERSION_CHANGED'; title: string }
  | { type: 'REVIEW_UPDATED' }
  | { type: 'LINKED_FEEDBACK'; key: string }

export const INITIAL_FLOW_STATE: PendingItemsFlowState = {
  guardian: null,
  guardianRejected: false,
  activeItemKey: null,
  completed: false,
  receiptUrl: null,
  updatedTitle: null,
  linkedFeedbackKey: null,
}

export const reducePendingItemsFlow = (
  state: PendingItemsFlowState,
  event: PendingItemsFlowEvent
): PendingItemsFlowState => {
  switch (event.type) {
    case 'DECLARE_GUARDIAN':
      return { ...state, guardian: event.declaration, guardianRejected: false }
    case 'REJECT_GUARDIAN':
      return { ...state, guardianRejected: true, activeItemKey: null }
    case 'OPEN_ITEM':
      return { ...state, activeItemKey: event.itemKey, linkedFeedbackKey: null }
    case 'CLOSE_ITEM':
      return { ...state, activeItemKey: null }
    case 'SAVED':
      return {
        ...state,
        activeItemKey: null,
        completed: state.completed || event.completed,
        receiptUrl: state.receiptUrl ?? event.receiptUrl,
      }
    case 'VERSION_CHANGED':
      return { ...state, updatedTitle: event.title }
    case 'REVIEW_UPDATED':
      return { ...state, updatedTitle: null }
    case 'LINKED_FEEDBACK':
      return { ...state, linkedFeedbackKey: event.key }
  }
}

export const findPendingItem = (
  model: PendingItemsPublicModel,
  itemKey: string | null
): PendingItemsPublicItem | null =>
  itemKey
    ? (model.actionableItems.find((item) => item.itemKey === itemKey) ??
      model.blockingItems.find((item) => item.itemKey === itemKey) ??
      null)
    : null

export const isSubmitCompleted = (result: { status: string }): boolean =>
  result.status === PendingItemsHandoffStatus.COMPLETED

export const openBlankTab = (): Window | null => {
  const tab = window.open('', '_blank')
  if (tab) {
    tab.opener = null
  }
  return tab
}

export const navigateTab = (tab: Window | null, url: string): void => {
  if (tab) {
    tab.location.replace(url)
    return
  }
  window.open(url, '_blank', 'noopener,noreferrer')
}

export const stripTokenFromAddressBar = (path: string): void => {
  window.history.replaceState(window.history.state, '', path)
}
